/* Five Realms Leakage Assessment (125 max) */

const LEAKAGE = {
  title: 'Five Realms Leakage Assessment',
  subtitle: 'Sovereignty Score + Leakage Index · Baseline over last 90 days',
  realms: {
    physical: {
      max: 25,
      path: 'Dysregulation → Reliable vitality → Quiet authority',
      statements: [
        'I honor sleep/recovery even when demands are high.',
        'I respond to pain/fatigue/hunger early rather than overriding until crisis.',
        'My energy comes from rhythm and nourishment, not stimulants/adrenaline.',
        'I protect recovery blocks (movement, sunlight, stillness) as non‑negotiable.',
        'I make health decisions from internal authority (not constant outsourcing).'
      ]
    },
    mental: {
      max: 25,
      path: 'Defending mind → Clear perception → Transparent presence',
      statements: [
        'I tell the truth about my capacity without spinning the story.',
        'I can hold nuance under stress (not win/lose or strong/weak collapse).',
        'I notice when I intellectualize emotions to avoid impact.',
        'I update beliefs when reality contradicts them (no rigid identity defense).',
        'My thinking serves coherence more than image protection.'
      ]
    },
    emotional: {
      max: 25,
      path: 'Numbness/volatility → Honest affect → Charge moves without sticking',
      statements: [
        'I can name what I feel without defensiveness or performance.',
        'I have access to emotions beyond anger (grief, fear, joy, tenderness).',
        'I can hold charge without collapsing, numbing, or projecting.',
        'I metabolize my own stress rather than outsourcing it to others.',
        'My calm comes from groundedness, not disconnection.'
      ]
    },
    spiritual: {
      max: 25,
      path: 'Emptiness/bypass → Grounded devotion → Unwavering presence',
      statements: [
        'My success is connected to real meaning/devotion (not empty achievement).',
        'I have an orienting practice that is lived (not performative).',
        'I can be still without compulsive stimulation or productivity.',
        'My purpose does not extract from me or others.',
        'My attention is intentionally allocated (not scattered/reaction-based).'
      ]
    },
    relational: {
      max: 25,
      path: 'Pursue/withdraw → Consent clarity → Devotional union',
      statements: [
        'I hold clear boundaries and do not assume access to others\' time/energy.',
        'I receive feedback without interpreting it as an attack.',
        'I repair ruptures cleanly rather than substituting distance/explanations.',
        'I use power appropriately in relationship (not hierarchy by default).',
        'I build trust through reliability rather than demanding loyalty.'
      ]
    }
  }
};

function buildRows(tbodyId, realmKey, statements){
  const tbody = document.getElementById(tbodyId);
  tbody.innerHTML = '';
  statements.forEach((stmt, idx) => {
    const q = idx + 1;
    const name = `${realmKey}_${q}`;
    const tr = document.createElement('tr');
    const tdStmt = document.createElement('td');
    tdStmt.className = '';
    tdStmt.textContent = stmt;
    tr.appendChild(tdStmt);

    for(let v=1; v<=5; v++){
      const td = document.createElement('td');
      const label = document.createElement('label');
      label.className = 'rate';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.value = String(v);
      input.required = true;
      const span = document.createElement('span');
      span.textContent = String(v);
      label.appendChild(input);
      label.appendChild(span);
      td.appendChild(label);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  });
}

function realmTotal(realmKey, count){
  let total = 0;
  for(let i=1;i<=count;i++){
    const el = document.querySelector(`input[name="${realmKey}_${i}"]:checked`);
    if(el) total += Number(el.value);
  }
  return total;
}

function update(){
  const scores = {};
  for(const [k, realm] of Object.entries(LEAKAGE.realms)){
    scores[k] = realmTotal(k, realm.statements.length);
    document.getElementById(`${k}Total`).textContent = String(scores[k]);
  }

  const total = Object.values(scores).reduce((a,b)=>a+b,0);
  const leakageIndex = 125 - total;
  const lostPercent = Math.round((leakageIndex/125)*100);

  document.getElementById('totalScore').textContent = String(total);
  document.getElementById('leakageIndex').textContent = String(leakageIndex);
  document.getElementById('lostPercent').textContent = String(lostPercent);

  const lockEl = document.getElementById('patternLock');

  // IMPORTANT: querySelectorAll('[required]') does NOT work reliably on dynamic radio-groups.
  // We compute completion by checking that each question-group has a checked value.
  const realmKeys = Object.keys(LEAKAGE.realms);
  let allAnswered = true;

  for (const rk of realmKeys) {
    const qCount = LEAKAGE.realms[rk].statements.length;
    for (let i = 1; i <= qCount; i++) {
      const el = document.querySelector(`input[name="${rk}_${i}"]:checked`);
      if (!el) {
        allAnswered = false;
        break;
      }
    }
    if (!allAnswered) break;
  }

  // Pattern lock should never show '-' once complete.
  // Always render as: Realm Name (score/max)
  let lowestKey = null;
  if (allAnswered) {
    let lowestPct = Infinity;
    for (const [k, realm] of Object.entries(LEAKAGE.realms)) {
      const pct = scores[k] / realm.max;
      if (pct < lowestPct) {
        lowestPct = pct;
        lowestKey = k;
      }
    }

    const names = {
      physical: 'Physical',
      mental: 'Mental',
      emotional: 'Emotional',
      spiritual: 'Spiritual/Energetic',
      relational: 'Relational'
    };

    const max = LEAKAGE.realms[lowestKey].max;
    lockEl.textContent = `${names[lowestKey]} (${scores[lowestKey]}/${max})`;
  } else {
    lockEl.textContent = 'Complete all items to reveal your lowest realm.';
  }

  return { scores, total, leakageIndex, lostPercent, ready: allAnswered, lowestKey };
}

function buildReport(state){
  const realmOrder = ['physical','mental','emotional','spiritual','relational'];
  const realmNames = {
    physical:'Physical',
    mental:'Mental',
    emotional:'Emotional',
    spiritual:'Spiritual/Energetic',
    relational:'Relational'
  };

  const summaryHtml = `
    <h2 class="h2">Executive Summary</h2>
    <div class="kv">
      <div class="totalBox"><div class="small">Sovereignty score</div><b>${state.total}</b> / 125</div>
      <div class="totalBox"><div class="small">Leakage index</div><b>${state.leakageIndex}</b></div>
      <div class="totalBox"><div class="small">% potential being lost</div><b>${state.lostPercent}%</b></div>
      <div class="totalBox"><div class="small">Pattern lock (lowest realm)</div><b>${realmNames[state.lowestKey]} (${state.scores[state.lowestKey]}/${LEAKAGE.realms[state.lowestKey].max})</b></div>
    </div>
  `;

  const realmRowsHtml = realmOrder.map(k => {
    const max = LEAKAGE.realms[k].max;
    const score = state.scores[k];
    const pct = Math.round((score/max)*100);
    const path = LEAKAGE.realms[k].path;
    return `<tr>
      <td><b>${realmNames[k]}</b></td>
      <td>${score}/${max}</td>
      <td>${pct}%</td>
      <td>${fortifyEscapeHtml(path)}</td>
    </tr>`;
  }).join('');

  const extraHtml = `
    <div class="hr"></div>
    <h2 class="h2">What to do next (C3 → Proof)</h2>
    <div class="quote">
      <b>CLEAR</b>: Name the leak without self‑attack. Retire false containers.<br>
      <b>CONNECT</b>: Restore contact with body signal and relationship truth.<br>
      <b>CREATE</b>: Build governance through proof vectors.
    </div>

    <h2 class="h2" style="margin-top:18px;">Applied Sovereignty proof vectors</h2>
    <ul class="small">
      <li>Agreements · Boundaries · Schedules · Roles</li>
      <li>Repair Protocols (within 72 hours) · Governance Structures</li>
      <li>Sustained Silence · Non‑Participation</li>
    </ul>
  `;

  // Optional $ cost (if provided)
  const highValue = Number(document.getElementById('highValueUnit')?.value || 0);
  const costPerUnit = highValue > 0 ? (highValue * (state.leakageIndex / 125)) : 0;

  const monthly = costPerUnit * 1;
  const quarterly = costPerUnit * 3;
  const annual = costPerUnit * 12;

  const moneyHtml = `
    <div class="hr"></div>
    <h2 class="h2">Leakage → $ Cost (optional)</h2>
    <p class="p">Estimates assume 1 high‑value unit per month (then expanded to quarter/year). If your cadence differs, adjust your unit definition accordingly.</p>
    <table class="table" aria-label="Leakage cost">
      <tbody>
        <tr><td><b>High‑value unit</b></td><td><b>${highValue > 0 ? ('$' + highValue.toFixed(2)) : 'Not provided'}</b></td></tr>
        <tr><td><b>Estimated cost per unit</b><div class="small">unit × (leakageIndex ÷ 125)</div></td><td><b>${highValue > 0 ? ('$' + costPerUnit.toFixed(2)) : '—'}</b></td></tr>
        <tr><td><b>Monthly estimate</b></td><td><b>${highValue > 0 ? ('$' + monthly.toFixed(2)) : '—'}</b></td></tr>
        <tr><td><b>Quarterly estimate</b></td><td><b>${highValue > 0 ? ('$' + quarterly.toFixed(2)) : '—'}</b></td></tr>
        <tr><td><b>Annual estimate</b></td><td><b>${highValue > 0 ? ('$' + annual.toFixed(2)) : '—'}</b></td></tr>
      </tbody>
    </table>
  `;

  openBrandedReportWindow({
    title: LEAKAGE.title,
    subtitle: LEAKAGE.subtitle,
    summaryHtml,
    realmRowsHtml,
    extraHtml: extraHtml + moneyHtml,
    footerText: 'DeanHobson.com | The Sovereignty Path'
  });
}

document.addEventListener('DOMContentLoaded', () => {
  buildRows('physicalRows','physical',LEAKAGE.realms.physical.statements);
  buildRows('mentalRows','mental',LEAKAGE.realms.mental.statements);
  buildRows('emotionalRows','emotional',LEAKAGE.realms.emotional.statements);
  buildRows('spiritualRows','spiritual',LEAKAGE.realms.spiritual.statements);
  buildRows('relationalRows','relational',LEAKAGE.realms.relational.statements);

  const form = document.getElementById('leakageForm');
  form.addEventListener('change', update);
  update();

  // Money calculator UI
  const hv = document.getElementById('highValueUnit');
  const est = document.getElementById('estimatedCost');
  const estMonthly = document.getElementById('estimatedMonthly');
  const estQuarterly = document.getElementById('estimatedQuarterly');
  const estAnnual = document.getElementById('estimatedAnnual');
  const btnCalc = document.getElementById('btnCalcCost');
  const btnClear = document.getElementById('btnClearCost');

  function setMoneyOutputs(costPerUnit){
    const per = Number(costPerUnit || 0);
    if (est) est.textContent = '$' + per.toFixed(2);
    if (estMonthly) estMonthly.textContent = '$' + (per * 1).toFixed(2);
    if (estQuarterly) estQuarterly.textContent = '$' + (per * 3).toFixed(2);
    if (estAnnual) estAnnual.textContent = '$' + (per * 12).toFixed(2);
  }

  btnCalc?.addEventListener('click', () => {
    const state = update();
    const highValue = Number(hv?.value || 0);
    const costPerUnit = highValue > 0 ? (highValue * (state.leakageIndex / 125)) : 0;
    setMoneyOutputs(costPerUnit);
  });

  btnClear?.addEventListener('click', () => {
    if (hv) hv.value = '';
    setMoneyOutputs(0);
  });

  let hasCalculatedResults = false;

  document.getElementById('btnViewReport')?.addEventListener('click', () => {
    const state = update();
    hasCalculatedResults = !!state.ready;
    if(!hasCalculatedResults){
      alert('Complete all items to view the full report.');
      return;
    }
    buildReport(state);
  });
});